import { useRef, useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import styles from './Carousel.module.scss';

const CAROUSEL_PAGE_WIDTH_PX = 550;

export interface CarouselProps {
  title?: string;
  subtitle?: string;
  /** Acción opcional a la derecha del título (ej. "View more") */
  headerAction?: ReactNode;
  children: ReactNode;
  /** Mostrar header con título y subtítulo. Por defecto true si hay title. */
  showHeader?: boolean;
  /** Mostrar paginación y botones prev/next. Por defecto true. */
  showControls?: boolean;
  /** Mostrar degradados de fade a los lados. Por defecto true. */
  showFades?: boolean;
  /** En desktop, las cards ocupan todo el ancho visible del carrusel. Por defecto false. */
  fullWidthCards?: boolean;
  /** En móvil, las cards tienen el mismo ancho que las de FeaturedStats (75%). Por defecto false. */
  statsCardWidth?: boolean;
  className?: string;
}

export function Carousel({
  title,
  subtitle,
  headerAction,
  children,
  showHeader = !!title,
  showControls = true,
  showFades = true,
  fullWidthCards = false,
  statsCardWidth = false,
  className,
}: CarouselProps) {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const isButtonScrollingRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref so button handlers can call the latest handleScroll without stale closure
  const handleScrollRef = useRef<() => void>(() => {});

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isButtonScrollingRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftFade(scrollLeft > 1);
      setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);

      const scrollableWidth = scrollWidth - clientWidth;
      const calcTotal = Math.max(1, Math.ceil(scrollableWidth / CAROUSEL_PAGE_WIDTH_PX) + 1);
      const calcCurrent = Math.min(Math.round(scrollLeft / CAROUSEL_PAGE_WIDTH_PX), calcTotal - 1);
      setTotalPages(calcTotal);
      setCurrentPage(calcCurrent);
    };

    handleScrollRef.current = handleScroll;
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current !== null) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  const scheduleScrollSync = () => {
    if (scrollTimerRef.current !== null) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      isButtonScrollingRef.current = false;
      handleScrollRef.current();
      scrollTimerRef.current = null;
    }, 600);
  };

  const handlePrev = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    isButtonScrollingRef.current = true;
    setShowLeftFade(container.scrollLeft - CAROUSEL_PAGE_WIDTH_PX > 0);
    setShowRightFade(true);
    setCurrentPage((p) => Math.max(0, p - 1));
    container.scrollBy({ left: -CAROUSEL_PAGE_WIDTH_PX, behavior: 'smooth' });
    scheduleScrollSync();
  };

  const handleNext = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const newScrollLeft = container.scrollLeft + CAROUSEL_PAGE_WIDTH_PX;
    const isAtEnd = newScrollLeft + container.clientWidth >= container.scrollWidth - 1;
    isButtonScrollingRef.current = true;
    setShowLeftFade(true);
    setShowRightFade(!isAtEnd);
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
    container.scrollBy({ left: CAROUSEL_PAGE_WIDTH_PX, behavior: 'smooth' });
    scheduleScrollSync();
  };

  const handleDotClick = (pageIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const targetScrollLeft = pageIndex * CAROUSEL_PAGE_WIDTH_PX;
    isButtonScrollingRef.current = true;
    setCurrentPage(pageIndex);
    setShowLeftFade(targetScrollLeft > 0);
    setShowRightFade(targetScrollLeft + container.clientWidth < container.scrollWidth - 1);
    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    scheduleScrollSync();
  };

  return (
    <div className={cn(styles.carousel, className)}>
      {showHeader && (title || subtitle || headerAction) && (
        <div className={styles.carousel__header}>
          <div className={styles.carousel__headerRow}>
            {title && <h2 className={styles.carousel__title}>{title}</h2>}
            {headerAction && <div className={styles.carousel__headerAction}>{headerAction}</div>}
          </div>
          {subtitle && <p className={styles.carousel__subtitle}>{subtitle}</p>}
        </div>
      )}

      <div
        className={cn(
          styles.carousel__wrapper,
          showFades && showLeftFade && styles['carousel__wrapper--fadeLeft'],
          showFades && showRightFade && styles['carousel__wrapper--fadeRight']
        )}
      >
        <div
          className={cn(
            styles.carousel__scroll,
            fullWidthCards && styles['carousel__scroll--fullWidthCards'],
            !showControls && styles['carousel__scroll--noControls'],
            statsCardWidth && styles['carousel__scroll--statsCardWidth']
          )}
          ref={scrollContainerRef}
        >
          {children}
        </div>
      </div>

      {showControls && (
        <div className={styles.carousel__controls}>
          <div className={styles.carousel__pagination}>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  styles.carousel__dot,
                  index !== currentPage && styles['carousel__dot--inactive']
                )}
                onClick={() => handleDotClick(index)}
                aria-label={t('carousel.goToPage', { page: index + 1 })}
              />
            ))}
          </div>
          <div className={styles.carousel__nav}>
            <button
              type="button"
              className={styles.carousel__navBtn}
              onClick={handlePrev}
              disabled={!showLeftFade}
              aria-label={t('carousel.previous')}
            >
              <Icon icon={ArrowLeft} size="md" />
            </button>
            <button
              type="button"
              className={styles.carousel__navBtn}
              onClick={handleNext}
              disabled={!showRightFade}
              aria-label={t('carousel.next')}
            >
              <Icon icon={ArrowRight} size="md" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

Carousel.displayName = 'Carousel';
